import { Colord, colord, extend } from 'colord';
import a11yPlugin from 'colord/plugins/a11y';
import mixPlugin from 'colord/plugins/mix';
import namesPlugin from 'colord/plugins/names';

extend([namesPlugin, mixPlugin, a11yPlugin]);

type Color = string;
type RGBAParsed = {
  r: number;
  g: number;
  b: number;
  a: number;
};

export const toRgbString = (color: Color) => colord(color).toRgbString();
export const getAlpha = (color: Color) => colord(color).alpha();
export const getColorWithAlphaString = (color: Color, alpha: number) => colord(color).alpha(alpha).toRgbString();
export const getColorWithAlphaStringHex = (color: Color, alpha: number) => colord(color).alpha(alpha).toHex();
export const removeHexAlpha = (color: Color) => colord(color).alpha(1).toHex();
export const removeAlpha = (color: Color) => colord(color).alpha(1).toRgbString();
export const parseColorToRgba = (color: Color): RGBAParsed => colord(color).rgba;
export const toHex = (color: Color) => colord(color).toHex();
export const isTransparent = (color: Color) => colord(color).alpha() === 0;
export const isLight = (color: Color) => colord(color).isLight();
export const getLuminance = (color: Color) => colord(color).luminance();

/**
 * Mixes all colours together and returns the resulting colour
 *
 * @param colors Array of colors to mix
 * @returns New colour with all colours mixed together
 */
export const mixColors = (colors: string[]) => {
  let mixedColor = colord(colors[0]);
  for (let i = 1; i < colors.length; i++) {
    mixedColor = mixedColor.mix(colors[i], 1 / (i + 1));
  }
  return mixedColor.toHex();
};

/**
 * Check if `fgColor` is readable against `bgColor`
 *
 * @param bgColor background color
 * @param fgColor foreground color
 * @returns boolean - true fgColor is readable against bgColor
 */
export const isReadable = (bgColor: Color, fgColor: Color) => colord(fgColor).isReadable(bgColor);

/**
 * Returns a color with the same alpha as the old color
 *
 * @param oldColor old color
 * @param newColor new color with same alpha as old color
 */
export const maintainAlpha = (oldColor: Color, newColor: Color) => {
  const alpha = getAlpha(oldColor);
  return getColorWithAlphaString(newColor, alpha);
};

/**
 * Generate a palette with tints and shades of `baseColor`
 *
 * @param baseColor
 * @returns array of hex colour strings
 */
export const generatePaletteFromColor = (baseColor: Color) => [
  ...new Set([
    ...colord(baseColor)
      .tints()
      .map((c) => c.toHex().toUpperCase()),
    ...colord(baseColor)
      .shades()
      .map((c) => c.toHex().toUpperCase()),
  ]),
];

/**
 * Filter the colors on the palette
 * #1 Filter duplicated color
 *
 * @param palette
 * @returns array of colors
 */
export const getFilteredPalette = (palette: string[]): string[] =>
  palette
    .filter((color, idx, arr) => color && idx === arr.indexOf(color))
    .filter((color) => {
      // Convert the color to an RGB object using colord
      const rgbObject: Colord = colord(color);
      // Extract the RGB values as an array
      const rgbArray: number[] = [rgbObject.toRgb().r, rgbObject.toRgb().g, rgbObject.toRgb().b];
      // Ignore grayscale within 15 RGB values of each other
      return Math.max(...rgbArray) - Math.min(...rgbArray) > 15;
    });

/**
 * Given a background colour, find a color in `colorOptions` that will be readable against the background.
 * If no color in the list exists, return black or white.
 *
 * @param bgColor Background color of text - used to check readability
 * @param colorOptions List of colour options for the text
 * @returns Color from colorOptions that will be readable against bgColor, if no color is found, will return black or white
 */
export const getReadableTextColorFromList = (bgColor: string, colorOptions: string[]) => {
  const isLightColor = isLight(bgColor);

  for (const clr of colorOptions) {
    if (colord(clr).isReadable(bgColor)) {
      return clr;
    }
  }

  // Didn't find a readable colour, return black or white depending on bg colour
  return isLightColor ? '#000000' : '#FFFFFF';
};

/**
 * Determines the outline color based on the luminance of a given color.
 *
 * @param {string} color - The color to evaluate. This can be in either RGB or HEX format.
 * @returns {string} The contrasting outline color ('#000000' for light colors, '#ffffff' for dark colors).
 */
export const getOutlineColorBasedOnLuminance = (color: string): Color =>
  getLuminance(color) > 0.5 ? '#000000' : '#ffffff';

// All Color utility functions below are credited to E1

/**
 * Convert HSV to RGB value
 * @param {number} h , 0 <= value <= 1
 * @param {number} s , 0 <= value <= 1
 * @param {number} v , 0 <= value <= 1
 * @returns {object} rgb , 0 <= value <= 255
 */
export function VgHSVtoRGB(h: number, s: number, v: number) {
  let r: number = 0,
    g: number = 0,
    b: number = 0,
    i,
    f,
    p,
    q,
    t;
  i = Math.floor(h * 6);
  f = h * 6 - i;
  p = v * (1 - s);
  q = v * (1 - f * s);
  t = v * (1 - (1 - f) * s);
  switch (i % 6) {
    case 0:
      r = v;
      g = t;
      b = p;
      break;
    case 1:
      r = q;
      g = v;
      b = p;
      break;
    case 2:
      r = p;
      g = v;
      b = t;
      break;
    case 3:
      r = p;
      g = q;
      b = v;
      break;
    case 4:
      r = t;
      g = p;
      b = v;
      break;
    case 5:
      r = v;
      g = p;
      b = q;
      break;
  }
  return {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(b * 255),
  };
}

/**
 * Convert aValue on range [rangeMinA, rangeMaxA] to what it'd be on [rangeMinB, rangeMaxB]
 * with same percentage. For e.x: if aValue is 50% in rangeA, then return what 50% is on rangeB.
 * @param {number} rangeMinA
 * @param {number} rangeMaxA
 * @param {number} rangeMinB
 * @param {number} rangeMaxB
 * @param {number} aValue
 */
export function getValueOnDifferentRangeWithSamePercentage(
  valueOnRangeA: number,
  rangeMinA: number,
  rangeMaxA: number,
  rangeMinB: number,
  rangeMaxB: number,
) {
  const percentageOnRangeA = (valueOnRangeA - rangeMinA) / (rangeMaxA - rangeMinA);
  const valueOnRangeB = (rangeMaxB - rangeMinB) * percentageOnRangeA + rangeMinB;
  return valueOnRangeB;
}

/**
 * Convert tintSlider value to rgb color
 * @param {*} tintValue from slider
 * @param {*} intensity of tint as percentage
 * @returns {object} rgb e.x: {r:2, g:2, b:2}
 */
export function convertTintToFeFuncRgbIntercept(tintValue: number, intensity = 50) {
  if (!tintValue) return null;
  // TODO: move these to shared lib constants file.
  // can't do right now because core is not setup to use @venngage packages.
  const MAX_TINT_SLIDER_VALUE = 100;
  const MIN_TINT_SLIDER_VALUE = -100;
  const MAX_FEFUNC_RGB_INTERCEPT = (0.3 * intensity) / 100;
  const MIN_FEFUNC_RGB_INTERCEPT = 0.0;
  const MAX_HUE_DECIMAL = 1.0;
  const MIN_HUE_DECIMAL = 0.0;
  const MIN_RGB = 0;
  const MAX_RGB = 255;
  // convert slider[-100<0<100] to hueDecimal[0<0.5<1.0]
  let hueInDecimal = getValueOnDifferentRangeWithSamePercentage(
    tintValue,
    MIN_TINT_SLIDER_VALUE,
    MAX_TINT_SLIDER_VALUE,
    MIN_HUE_DECIMAL,
    MAX_HUE_DECIMAL,
  );
  const rgb = VgHSVtoRGB(hueInDecimal, 1.0, 1.0); // max out saturation and value to only look at hue.
  const feFuncRgbIntercept = {
    r: getValueOnDifferentRangeWithSamePercentage(
      rgb.r,
      MIN_RGB,
      MAX_RGB,
      MIN_FEFUNC_RGB_INTERCEPT,
      MAX_FEFUNC_RGB_INTERCEPT,
    ),
    g: getValueOnDifferentRangeWithSamePercentage(
      rgb.g,
      MIN_RGB,
      MAX_RGB,
      MIN_FEFUNC_RGB_INTERCEPT,
      MAX_FEFUNC_RGB_INTERCEPT,
    ),
    b: getValueOnDifferentRangeWithSamePercentage(
      rgb.b,
      MIN_RGB,
      MAX_RGB,
      MIN_FEFUNC_RGB_INTERCEPT,
      MAX_FEFUNC_RGB_INTERCEPT,
    ),
  };

  return feFuncRgbIntercept;
}

/**
 * get white or dark alpha color that have enough contract ratio with the given color
 */
export const getWhiteOrDarkAlphaColor = (color: string, alpha: number = 0.5) => {
  if (colord(color).contrast('#000') > colord(color).contrast('#FFF')) {
    return colord('#000').alpha(alpha).toRgbString();
  }

  return colord('#FFF').alpha(alpha).toRgbString();
};

/**
 * Determines if a given color is grayscale.
 *
 * A color is considered grayscale if its red, green, and blue components are equal,
 * indicating no hue and saturation but only brightness. This function uses the `colord`
 * library to parse and analyze the color. It accepts color in any format supported by `colord`
 * (e.g., HEX, RGB, HSL, etc.) and returns true if the color is grayscale, false otherwise.
 *
 * @param {string} color - The color to be checked, in any format that can be parsed by `colord`.
 * @returns {boolean} True if the color is grayscale, false otherwise.
 */
export const isGrayScale = (color: string): boolean => {
  if (color === '' || !colord(color).isValid()) return false;

  const parsedColor: Colord = colord(color);
  const { r, g, b } = parsedColor.toRgb();
  return r === g && r === b;
};
